import { createSelector } from '@reduxjs/toolkit';
import { selectCmsFromBootstrap, selectCurrencyCodeFromBootstrap, selectLocaleFromBootstrap } from 'store/slice/bootstrap';
import { PriceFormatConfiguration } from '@agoda/react-handyman/lib/utils/priceFormatter/types';
import { ActivityPriceFormat } from 'gql/service/graph/activityService';
import { PriceFormatter, StringFormatter } from '@agoda/react-handyman';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { PriceFilterType, RangeFilter, RangeFilterEvents, ValueFilter, ValueFilterEvents } from 'core/type/Filter.types';
import { CategoryValueFilter, CategoryWithSubcategoryValueFilter, SubcategoryValueFilter } from 'component/page/search/SortAndFilter/CategoryFilter/CategoryFilter.types';
import { CategoryId } from 'core/type/Category.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2,
    ActivitiesSearchSelectedFilterClearAllButtonClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2,
    ActivitiesSearchSelectedFilterClearAllButtonClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { FilterCode } from 'gql/autogen/FilterCode';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { SubcategoryId } from 'core/type/Subcategory.types';

export interface OwnProps extends ValueFilterEvents, RangeFilterEvents {
    cms: CmsValue;
    priceFilter?: PriceFilterType;
    categoryFilter: CategoryValueFilter[];
    categoryWithSubcategoryFilter: CategoryWithSubcategoryValueFilter[];
    subcategoryFilter: SubcategoryValueFilter[];
    starRatingFilter: RangeFilter[];
    durationFilter: ValueFilter[];
    bookingFeatureFilter: ValueFilter[];
    onCategoriesChange: (categories: CategoryId[], index: number, count: number) => void;
    onCategoryWithSubcategoryChange: (value: CategoryId, selected: boolean) => void;
    onSubcategoryFilterChange: (values: SubcategoryId, selected: boolean) => void;
    onClearSortAndFilter: () => void;
    isSidebar: boolean;
}

interface StateProps {
    selectedPriceFilterText: string;
    isShowPriceFilter: boolean;
    selectedCategoryFilter: CategoryValueFilter[];
    selectedCategoryWithSubcategoryFilter: CategoryWithSubcategoryValueFilter[];
    selectedSubcategoryFilter: SubcategoryValueFilter[];
    selectedStarRatingFilter: RangeFilter[];
    selectedDurationFilter: ValueFilter[];
    selectedBookingFeatureFilter: ValueFilter[];
    hasFilter: boolean;
    isSubcategoryFilterEnabled: boolean;
}

interface DispatchProps {
    handleClearAllFilter: () => void;
    handleClearCategoryFilter: () => void;
    handleClearCategoryWithSubcategoryFilter: (value: CategoryId) => void;
    handleClearSubcategoryFilter: (value: SubcategoryId) => void;
    handleClearPriceFilter: () => void;
    handleClearStarRatingFilter: (starRating: RangeFilter) => void;
    handleClearValueFilter: (values: string[], code: FilterCode) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    [selectCmsFromBootstrap, selectLocaleFromBootstrap, selectCurrencyCodeFromBootstrap, (_, props: OwnProps) => props],
    (cmsValue, locale, currencyCode, props): StateProps => {
        const priceFormatConfig: PriceFormatConfiguration = { ...ActivityPriceFormat, currency: currencyCode, decimalPlaces: 0 };

        const formattedMinPrice = PriceFormatter.format(props.priceFilter?.selectedMin || 0, locale, priceFormatConfig);
        const formattedMaxPrice = PriceFormatter.format(props.priceFilter?.selectedMax || 0, locale, priceFormatConfig);

        const selectedPriceFilterText = StringFormatter.format(cmsValue[264337], formattedMinPrice, formattedMaxPrice);
        const isShowPriceFilter = props.priceFilter ? props.priceFilter.selectedMin !== props.priceFilter.min || props.priceFilter.selectedMax !== props.priceFilter.max : false;

        const isSubcategoryFilterEnabled = isOn(FeatureName.SubcategoryFilter);

        const selectedCategoryFilter = props.categoryFilter.filter((category) => category.selected && category.values.length > 0);
        const selectedCategoryWithSubcategoryFilter = props.categoryWithSubcategoryFilter.filter((category) => category.selected);
        const selectedStarRatingFilter = props.starRatingFilter.filter((starRating) => starRating.selected);
        const selectedDurationFilter = props.durationFilter.filter((duration) => duration.selected);
        const selectedBookingFeatureFilter = props.bookingFeatureFilter.filter((bookingFeature) => bookingFeature.selected);
        const selectedSubcategoryFilter = props.subcategoryFilter.filter((subcategory) => subcategory.selected);

        const hasFilter =
            isShowPriceFilter ||
            selectedCategoryFilter.length > 0 ||
            selectedStarRatingFilter.length > 0 ||
            selectedDurationFilter.length > 0 ||
            selectedBookingFeatureFilter.length > 0 ||
            selectedCategoryWithSubcategoryFilter.length > 0 ||
            selectedSubcategoryFilter.length > 0;

        return {
            isShowPriceFilter,
            selectedPriceFilterText,
            selectedCategoryFilter,
            selectedStarRatingFilter,
            selectedDurationFilter,
            selectedBookingFeatureFilter,
            selectedCategoryWithSubcategoryFilter,
            selectedSubcategoryFilter,
            hasFilter,
            isSubcategoryFilterEnabled,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function, props: OwnProps): DispatchProps => ({
    handleClearAllFilter: () => {
        trackElementEvent<ActivitiesSearchSelectedFilterClearAllButtonClickEventActivitiesFieldsV2>(
            ActivitiesSearchSelectedFilterClearAllButtonClickEventActivitiesManifestV2.ids,
            { is_sidebar: props.isSidebar },
        );
        props.onClearSortAndFilter();
    },
    handleClearCategoryFilter: () => {
        trackElementEvent<ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2>(ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2.ids, {
            is_sidebar: props.isSidebar,
            selected_filters: 'category',
        });
        props.onCategoriesChange([], -1, -1);
    },
    handleClearCategoryWithSubcategoryFilter: (value: CategoryId) => {
        trackElementEvent<ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2>(
            ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2.ids,
            {
                is_sidebar: props.isSidebar,
                selected_filters: 'category',
            },
            { action_element_value: value.toString() },
        );
        props.onCategoryWithSubcategoryChange(value, false);
    },
    handleClearSubcategoryFilter: (value: SubcategoryId) => {
        trackElementEvent<ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2>(
            ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2.ids,
            {
                is_sidebar: props.isSidebar,
                selected_filters: 'subcategory',
            },
            { action_element_value: value.toString() },
        );
        props.onSubcategoryFilterChange(value, false);
    },
    handleClearPriceFilter: () => {
        if (props.priceFilter) {
            trackElementEvent<ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2>(ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2.ids, {
                is_sidebar: props.isSidebar,
                selected_filters: 'price',
            });
            props.onRangeFilterChange(FilterCode.Price, { value: props.priceFilter.min, isInclusive: true }, { value: props.priceFilter.max, isInclusive: true }, true);
        }
    },
    handleClearStarRatingFilter: (starRatingFilter: RangeFilter) => {
        trackElementEvent<ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2>(
            ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2.ids,
            { is_sidebar: props.isSidebar, selected_filters: 'starRating' },
            { action_element_value: starRatingFilter.min.toString() },
        );
        props.onRangeFilterChange(FilterCode.Rating, { value: starRatingFilter.min, isInclusive: true }, { value: starRatingFilter.max, isInclusive: false }, false);
    },
    handleClearValueFilter: (values: string[], code: FilterCode) => {
        trackElementEvent<ActivitiesSearchSelectedFilterChipClickEventActivitiesFieldsV2>(
            ActivitiesSearchSelectedFilterChipClickEventActivitiesManifestV2.ids,
            { is_sidebar: props.isSidebar, selected_filters: code },
            { action_element_value: values.join(',') },
        );
        props.onValueFilterChange(code, values, false);
    },
});
